import type { Job } from '@/api/endpoints/jobs'

/**
 * The user-facing failure reason to surface in the jobs feed.
 *
 * Only jobs that ended in `failed` or `cancelled` carry a reason worth
 * showing — a job that succeeded (or is still running/pending) has nothing
 * to explain, so those return `null` and render no disclosure. The backend
 * stores the friendly, pre-truncated string under `job.error.message` (see
 * apps/backend/src/utils/friendly_errors.py); we read it defensively since
 * `error` is a free-form JSON blob in the schema and may be absent or
 * shaped differently on older rows.
 *
 * Returns `null` whenever there's nothing worth showing so callers can
 * gate the UI on a single truthy check.
 */
export function failureReason(job: Job): string | null {
  if (job.status !== 'failed' && job.status !== 'cancelled') return null
  const message = (job.error as { message?: unknown } | null)?.message
  if (typeof message !== 'string') return null
  const trimmed = message.trim()
  return trimmed.length > 0 ? trimmed : null
}
